import {WeatherStationFromList} from '@smartmixin/core';
import React from 'react';

const useSensorNames = (station: WeatherStationFromList) => {
  const getDefaultChannelName = React.useCallback((metric: string) => {
    const match = metric.match(/CH(\d+)/);
    if (match) {
      const chNum = match[1];
      // Distinguish temperature sensor types in the merged card
      if (metric.startsWith('TEMP_AND_HUMIDITY_')) {
        return `Ch${chNum} T+H`;
      }
      if (metric.startsWith('TEMP_CH')) {
        return `Ch${chNum} T`;
      }
      return `Ch${chNum}`;
    }
  }, []);

  const getGivenChannelName = React.useCallback(
    (metric: string) => {
      const sensorNames = station?.meta?.sensor_names || {};

      // Exact match (e.g. dashboard passes prefix keys like 'TEMP_AND_HUMIDITY_CH1_')
      if (sensorNames[metric]) {
        return sensorNames[metric];
      }

      // Legacy: temperature sensors stored names under exact key (e.g. 'TEMP_CH1_T')
      // but are now addressed by prefix ('TEMP_CH1_')
      if (metric.endsWith('_') && sensorNames[`${metric}T`]) {
        return sensorNames[`${metric}T`];
      }

      // Prefix match (e.g. charts pass full keys like 'TEMP_AND_HUMIDITY_CH1_T'
      // but sensor names are stored under the prefix key)
      const prefixKey = Object.keys(sensorNames).find(
        (key) => key.endsWith('_') && metric.startsWith(key),
      );
      return prefixKey ? sensorNames[prefixKey] : undefined;
    },
    [station?.meta?.sensor_names],
  );

  const getChannelName = React.useCallback(
    (metric: string) => {
      const sensorName = getGivenChannelName(metric);
      if (sensorName) {
        return sensorName;
      }

      return getDefaultChannelName(metric);
    },
    [getGivenChannelName, getDefaultChannelName],
  );

  return {getDefaultChannelName, getGivenChannelName, getChannelName};
};

export default useSensorNames;
